/**
 * Content Page - Content/Video performance analytics
 *
 * Shows:
 * - Summary cards (Total Videos, Total Calls, Total Cash Collected)
 * - Metric filter buttons for sorting
 * - Video cards grid with thumbnails and performance metrics
 * - Detailed metrics table
 */

import { useState, useEffect, useCallback, useMemo, type MouseEvent } from "react";
import { motion } from "framer-motion";
import { PageHeader, StatCard, StatCardSkeleton } from "../components";
import { useDateRange } from "../context/DateRangeContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

// Icons
const VideoIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const PhoneIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

const DollarIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const PercentIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
  </svg>
);

const TrendingUpIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
);

// Formatters
const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);

const formatNumber = (value: number) =>
  new Intl.NumberFormat("en-US").format(value);

const formatPercent = (value: number) => `${value.toFixed(1)}%`;

const UsersIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const InboxIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
  </svg>
);

// Metric filter options
const METRIC_FILTERS = [
  { value: "cashCollected", label: "Cash Collected", icon: DollarIcon },
  { value: "showRate", label: "Show Rate", icon: PercentIcon },
  { value: "closeRate", label: "Close Rate", icon: PercentIcon },
  { value: "aov", label: "AOV", icon: TrendingUpIcon },
  { value: "cashPerCall", label: "Cash per Call", icon: DollarIcon },
  { value: "avgCashPerApplication", label: "Cash/Application", icon: DollarIcon },
  { value: "avgCashPerOptin", label: "Cash/Opt-in", icon: DollarIcon },
  { value: "applications", label: "Top Applications", icon: UsersIcon },
  { value: "optins", label: "Top Opt-ins", icon: InboxIcon },
] as const;

type MetricFilterValue = typeof METRIC_FILTERS[number]["value"];

interface VideoMetrics {
  videoId: string;
  sourceContent: string;
  title: string;
  thumbnailUrl: string;
  videoUrl: string;
  totalCalls: number;
  callsTaken: number;
  callsDue: number;
  callsClosed: number;
  noShows: number;
  cashCollected: number;
  revenueGenerated: number;
  showRate: number;
  closeRate: number;
  noShowRate: number;
  aov: number;
  cashPerCall: number;
  applications: number;
  optins: number;
  bookedCalls: number;
  avgCashPerApplication: number;
  avgCashPerOptin: number;
  isNonYouTube?: boolean;
}

interface ContentTotals {
  totalVideos: number;
  totalCalls: number;
  totalCashCollected: number;
  totalRevenue: number;
  totalClosed: number;
  totalApplications: number;
  totalOptins: number;
}

const ITEMS_PER_PAGE = 8;

// Crimson color palette for chart bars
const CRIMSON_COLORS = [
  "#ef4444", "#dc2626", "#b91c1c", "#991b1b",
  "#f87171", "#fca5a5", "#c53030", "#e53e3e", "#fc8181", "#be2626",
];

type GroupBy = "day" | "month" | "quarter";

interface TimeseriesBucket {
  period: string;
  label: string;
  total: number;
  videos: Record<string, number>;
}

interface TimeseriesVideo {
  id: string;
  title: string;
}

interface TimeseriesData {
  buckets: TimeseriesBucket[];
  videoList: TimeseriesVideo[];
}

// Truncate long video titles for legend
const truncateTitle = (title: string, max = 30) =>
  title.length > max ? title.slice(0, max) + "…" : title;

const formatCash = (v: number) =>
  v >= 1000000 ? `$${(v / 1000000).toFixed(1)}M`
  : v >= 1000 ? `$${(v / 1000).toFixed(0)}K`
  : `$${v.toFixed(0)}`;

// Content Cash Timeseries Chart
function ContentCashTimeSeries({ data, groupBy, onGroupByChange }: {
  data: TimeseriesData | null;
  groupBy: GroupBy;
  onGroupByChange: (g: GroupBy) => void;
}) {
  const buckets = data?.buckets ?? [];
  const allVideos = data?.videoList ?? [];

  // Top 4 videos by total cash (for legend only)
  const topVideos = allVideos.slice(0, 4);
  const topVideoIds = new Set(topVideos.map(v => v.id));

  // Build recharts data: each row is a bucket, ALL video keys
  const chartData = buckets.map(b => {
    const row: Record<string, number | string> = { label: b.label };
    for (const v of allVideos) {
      row[v.id] = b.videos[v.id] || 0;
    }
    return row;
  });

  const isEmpty = buckets.length === 0;

  // Custom tooltip: top 5 by cash, then "Other" row summing the rest, then Total
  const CustomTooltip = ({ active, payload, label }: {
    active?: boolean;
    payload?: Array<{ dataKey: string; value: number; fill: string }>;
    label?: string;
  }) => {
    if (!active || !payload?.length) return null;
    const nonZero = payload.filter(p => (p.value ?? 0) > 0).sort((a, b) => b.value - a.value);
    if (!nonZero.length) return null;
    const total = nonZero.reduce((s, p) => s + p.value, 0);
    const top5 = nonZero.slice(0, 5);
    const otherSum = nonZero.slice(5).reduce((s, p) => s + p.value, 0);
    const fmt = (v: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v);
    return (
      <div className="bg-card border border-border rounded-xl shadow-2xl p-3" style={{ minWidth: "220px", maxWidth: "300px", zIndex: 50 }}>
        <p className="text-xs font-semibold text-muted-foreground mb-2">{label}</p>
        {top5.map(item => {
          const vid = allVideos.find(v => v.id === item.dataKey);
          const title = vid ? truncateTitle(vid.title, 35) : String(item.dataKey);
          return (
            <div key={String(item.dataKey)} className="flex items-center gap-2 mb-1.5">
              <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: item.fill }} />
              <span className="text-xs text-muted-foreground flex-1 leading-tight">{title}</span>
              <span className="text-xs font-bold text-foreground ml-2 tabular-nums">{fmt(item.value)}</span>
            </div>
          );
        })}
        {otherSum > 0 && (
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0 bg-muted-foreground/30" />
            <span className="text-xs text-muted-foreground flex-1">Other</span>
            <span className="text-xs font-semibold text-muted-foreground tabular-nums">{fmt(otherSum)}</span>
          </div>
        )}
        <div className="border-t border-border mt-2 pt-2 flex justify-between items-center">
          <span className="text-xs font-semibold text-foreground">Total</span>
          <span className="text-xs font-bold text-emerald-500 tabular-nums">{fmt(total)}</span>
        </div>
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="bg-card rounded-xl border border-border overflow-hidden"
    >
      <div className="px-5 pt-5 pb-3 flex items-center justify-between flex-wrap gap-3 border-b border-border/50">
        <div>
          <h3 className="text-base font-semibold">Cash Collected Over Time</h3>
          <p className="text-xs text-muted-foreground mt-0.5">All videos by revenue, top 4 shown in legend</p>
        </div>
        <div className="flex items-center gap-1 bg-muted/40 rounded-lg p-1">
          {(["day", "month", "quarter"] as GroupBy[]).map(g => (
            <button
              key={g}
              onClick={() => onGroupByChange(g)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all capitalize ${
                groupBy === g
                  ? "bg-[var(--brand-primary)] text-white shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {g}
            </button>
          ))}
        </div>
      </div>

      <div className="p-5">
        {isEmpty ? (
          <div className="flex items-center justify-center h-[280px] text-muted-foreground text-sm">
            No cash data for this period
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 8 }} barCategoryGap="25%">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" strokeOpacity={0.07} />
              <XAxis
                dataKey="label"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 11, fill: "currentColor", opacity: 0.6 }}
                tickMargin={8}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 11, fill: "currentColor", opacity: 0.55 }}
                width={52}
                tickFormatter={formatCash}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "currentColor", fillOpacity: 0.05 }} />
              <Legend
                wrapperStyle={{ fontSize: "11px", paddingTop: "12px" }}
                formatter={(value) => {
                  const vid = topVideos.find(v => v.id === value);
                  return truncateTitle(vid?.title || value, 30);
                }}
              />
              {allVideos.map((v, i) => (
                <Bar
                  key={v.id}
                  dataKey={v.id}
                  stackId="cash"
                  fill={CRIMSON_COLORS[i % CRIMSON_COLORS.length]}
                  radius={i === allVideos.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                  legendType={topVideoIds.has(v.id) ? "square" : "none"}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </motion.div>
  );
}

// Video Thumbnail component with proper loading states
function VideoThumbnail({ thumbnailUrl, title, videoUrl, isNonYouTube }: { thumbnailUrl: string; title: string; videoUrl: string; isNonYouTube?: boolean }) {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const showPlaceholder = !thumbnailUrl || imageError;

  return (
    <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)]">
      {/* Gradient placeholder is always the background */}
      {showPlaceholder && (
        <div className="absolute inset-0 flex items-center justify-center z-0">
          <div className="text-white/80">
            {isNonYouTube ? (
              // Social/other content icon (share/broadcast)
              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
            ) : (
              // YouTube play icon
              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
          </div>
        </div>
      )}

      {/* Actual thumbnail image - on top of placeholder */}
      {thumbnailUrl && !imageError && (
        <img
          src={thumbnailUrl}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-all duration-300 z-10"
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
        />
      )}

      {/* Overlay on hover - highest z-index */}
      {videoUrl && (
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center z-20">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-2 text-white text-sm font-medium">
            <ExternalLinkIcon />
            <span>Watch on YouTube</span>
          </div>
        </div>
      )}
    </div>
  );
}

type Platform = "youtube" | "instagram";

// 11-char YouTube ID detector (matches the backend regex used to flag videos)
const YOUTUBE_ID_REGEX = /^[a-zA-Z0-9_-]{11}$/;

const PLATFORM_META: Record<Platform, { label: string; subtitle: string }> = {
  youtube: {
    label: "YouTube",
    subtitle: "Performance metrics for your YouTube videos",
  },
  instagram: {
    label: "Instagram",
    subtitle: "Performance metrics for your Instagram content (Reels, Stories, posts)",
  },
};

const YouTubeIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <path d="M23.498 6.186a2.997 2.997 0 0 0-2.107-2.12C19.505 3.5 12 3.5 12 3.5s-7.505 0-9.391.566A2.997 2.997 0 0 0 .502 6.186C0 8.078 0 12 0 12s0 3.922.502 5.814a2.997 2.997 0 0 0 2.107 2.12C4.495 20.5 12 20.5 12 20.5s7.505 0 9.391-.566a2.997 2.997 0 0 0 2.107-2.12C24 15.922 24 12 24 12s0-3.922-.502-5.814zM9.6 15.6V8.4l6.4 3.6-6.4 3.6z" />
  </svg>
);

const InstagramIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <rect x="3" y="3" width="18" height="18" rx="5" strokeWidth={2} />
    <circle cx="12" cy="12" r="4" strokeWidth={2} />
    <circle cx="17.5" cy="6.5" r="1.2" fill="currentColor" stroke="none" />
  </svg>
);

export function Content() {
  const { formatForApi } = useDateRange();
  const [isLoading, setIsLoading] = useState(true);
  const [videos, setVideos] = useState<VideoMetrics[]>([]);
  const [totals, setTotals] = useState<ContentTotals | null>(null);
  const [sortBy, setSortBy] = useState<MetricFilterValue>("cashCollected");
  const [attribution, setAttribution] = useState<"last_touch" | "first_touch">("last_touch");
  const [platform, setPlatform] = useState<Platform>("youtube");
  const [currentPage, setCurrentPage] = useState(1);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // Timeseries state
  const [timeseriesData, setTimeseriesData] = useState<TimeseriesData | null>(null);
  const [isTimeseriesLoading, setIsTimeseriesLoading] = useState(true);
  const [groupBy, setGroupBy] = useState<GroupBy>("month");

  // Fetch data
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const apiDates = formatForApi();
      const params = new URLSearchParams();
      params.set("startDate", apiDates.startDate);
      params.set("endDate", apiDates.endDate);
      params.set("sortBy", sortBy);
      params.set("attribution", attribution);

      const response = await fetch(`/api/content/metrics?${params}`);
      const result = await response.json();

      if (result.success) {
        setVideos(result.data.videos);
        setTotals(result.data.totals);
      }
    } catch (error) {
      console.error("Failed to fetch content metrics:", error);
    } finally {
      setIsLoading(false);
    }
  }, [formatForApi, sortBy, attribution]);

  // Fetch timeseries data
  const fetchTimeseries = useCallback(async () => {
    setIsTimeseriesLoading(true);
    try {
      const apiDates = formatForApi();
      const params = new URLSearchParams();
      params.set("startDate", apiDates.startDate);
      params.set("endDate", apiDates.endDate);
      params.set("groupBy", groupBy);

      const response = await fetch(`/api/content/timeseries?${params}`);
      const result = await response.json();

      if (result.success) {
        setTimeseriesData(result.data);
      }
    } catch (error) {
      console.error("Failed to fetch content timeseries:", error);
    } finally {
      setIsTimeseriesLoading(false);
    }
  }, [formatForApi, groupBy]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchTimeseries();
  }, [fetchTimeseries]);

  // Reset page when sort or platform changes
  useEffect(() => {
    setCurrentPage(1);
  }, [sortBy, platform]);

  // Filter videos by selected platform.
  // YouTube tab: real 11-char YouTube IDs + __title_ pseudo-IDs (unresolved YouTube titles).
  // Instagram tab: __source_ pseudo-IDs (confirmed non-YouTube sources like Instagram, TikTok, etc.).
  const filteredVideos = useMemo(() => {
    return videos.filter((v) => {
      const isYouTube = YOUTUBE_ID_REGEX.test(v.videoId) || v.videoId.startsWith("__title_");
      return platform === "youtube" ? isYouTube : !isYouTube;
    });
  }, [videos, platform]);

  // Recompute totals from the filtered set so the stat cards match the active tab.
  const filteredTotals = useMemo<ContentTotals | null>(() => {
    if (!totals) return null;
    return {
      totalVideos: filteredVideos.length,
      totalCalls: filteredVideos.reduce((s, v) => s + v.totalCalls, 0),
      totalCashCollected: filteredVideos.reduce((s, v) => s + v.cashCollected, 0),
      totalRevenue: filteredVideos.reduce((s, v) => s + v.revenueGenerated, 0),
      totalClosed: filteredVideos.reduce((s, v) => s + v.callsClosed, 0),
      totalApplications: filteredVideos.reduce((s, v) => s + v.applications, 0),
      totalOptins: filteredVideos.reduce((s, v) => s + v.optins, 0),
    };
  }, [filteredVideos, totals]);

  // Filter timeseries by platform. Videos in the chart are keyed by the same
  // videoId scheme as the metrics endpoint. __title_ pseudo-IDs are unresolved YouTube titles.
  const filteredTimeseriesData = useMemo<TimeseriesData | null>(() => {
    if (!timeseriesData) return null;
    const isYouTubeId = (id: string) => YOUTUBE_ID_REGEX.test(id) || id.startsWith("__title_");
    const keep = (id: string) => (platform === "youtube" ? isYouTubeId(id) : !isYouTubeId(id));
    const videoList = timeseriesData.videoList.filter((v) => keep(v.id));
    const keptIds = new Set(videoList.map((v) => v.id));
    const buckets = timeseriesData.buckets
      .map((b) => {
        const videos: Record<string, number> = {};
        let total = 0;
        for (const id of keptIds) {
          const cash = b.videos[id] || 0;
          videos[id] = cash;
          total += cash;
        }
        return { period: b.period, label: b.label, total, videos };
      })
      .filter((b) => b.total > 0);
    return { buckets, videoList };
  }, [timeseriesData, platform]);

  const slugify = (value: string) =>
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60);

  const handleDownloadCsv = async (video: VideoMetrics, event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    try {
      setDownloadingId(video.videoId);
      const { startDate, endDate } = formatForApi();
      const params = new URLSearchParams({
        startDate,
        endDate,
        videoId: video.videoId,
      });
      const response = await fetch(`/api/content/lead-export?${params.toString()}`);
      if (!response.ok) {
        throw new Error("Failed to export CSV");
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const safeTitle = slugify(video.title || video.videoId);
      link.href = url;
      link.download = `content-leads-${safeTitle || video.videoId}-${startDate}-${endDate}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download CSV:", error);
    } finally {
      setDownloadingId(null);
    }
  };

  // Pagination (over the platform-filtered set)
  const totalPages = Math.max(1, Math.ceil(filteredVideos.length / ITEMS_PER_PAGE));
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentVideos = filteredVideos.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Get close rate color: green if ≥30%, red if <30%
  const getCloseRateColor = (rate: number) => {
    return rate >= 30 ? "text-emerald-600 dark:text-emerald-400" : "text-red-500 dark:text-red-400";
  };

  // Get show rate color: green if ≥70%, red if <70%
  const getShowRateColor = (rate: number) => {
    return rate >= 70 ? "text-emerald-600 dark:text-emerald-400" : "text-red-500 dark:text-red-400";
  };

  // Only show skeleton on initial load, not when filtering (keep old data visible)
  if (isLoading && !totals) {
    return (
      <div className="space-y-6">
        <PageHeader title="Content Performance" subtitle="Track performance metrics for your Social Media content" />

        {/* Stats Summary Skeleton */}
        <div className="grid gap-4 md:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <StatCardSkeleton key={i} index={i} />
          ))}
        </div>

        {/* Filter Skeleton */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-9 gap-2">
          {METRIC_FILTERS.map((_, i) => (
            <div key={i} className="h-10 bg-muted rounded-lg animate-pulse" />
          ))}
        </div>

        {/* Video Grid Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <Card key={i} className="overflow-hidden">
              <div className="aspect-video bg-muted animate-pulse" />
              <CardContent className="p-4 space-y-3">
                <div className="h-5 bg-muted rounded animate-pulse w-3/4" />
                <div className="space-y-2">
                  {[0, 1, 2, 3].map((j) => (
                    <div key={j} className="flex justify-between">
                      <div className="h-4 bg-muted rounded animate-pulse w-24" />
                      <div className="h-4 bg-muted rounded animate-pulse w-16" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const platformMeta = PLATFORM_META[platform];

  const PlatformTabs = (
    <div className="inline-flex items-center rounded-lg border border-border bg-secondary/30 p-1 gap-1">
      {(["youtube", "instagram"] as const).map((p) => {
        const isActive = platform === p;
        const Icon = p === "youtube" ? YouTubeIcon : InstagramIcon;
        return (
          <button
            key={p}
            onClick={() => setPlatform(p)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-all duration-150 ${
              isActive
                ? "bg-[var(--brand-primary)] text-white shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Icon />
            <span>{PLATFORM_META[p].label}</span>
          </button>
        );
      })}
    </div>
  );

  if (videos.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader title="Content Performance" subtitle={platformMeta.subtitle} />
        {PlatformTabs}

        <div className="flex items-center justify-center p-12">
          <div className="text-center">
            <VideoIcon />
            <p className="text-lg font-medium mt-4">No video data found</p>
            <p className="text-sm text-muted-foreground mt-2">
              Add YouTube video IDs to your call data to see metrics here
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Content Performance" subtitle={platformMeta.subtitle} />

      {/* Platform Tabs (YouTube / Instagram) */}
      {PlatformTabs}

      {filteredVideos.length === 0 && (
        <div className="flex items-center justify-center p-10 rounded-xl border border-dashed border-border bg-card/50">
          <div className="text-center">
            <p className="text-sm font-medium">No {platformMeta.label} content in the selected range</p>
            <p className="text-xs text-muted-foreground mt-1">
              {platform === "instagram"
                ? "Once utm_source=Instagram (or non-YouTube utm_content) starts flowing in, content will appear here."
                : "Try widening the date range or check that utm_content contains valid YouTube IDs."}
            </p>
          </div>
        </div>
      )}

      {/* Attribution Model Toggle */}
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground">Attribution:</span>
        <div className="flex items-center rounded-lg border border-border bg-secondary/30 p-0.5 gap-0.5">
          {(["last_touch", "first_touch"] as const).map((mode) => (
            <button
              key={mode}
              onClick={() => setAttribution(mode)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-150 ${
                attribution === mode
                  ? "bg-[var(--brand-primary)] text-white shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {mode === "last_touch" ? "Last Touch" : "First Touch"}
            </button>
          ))}
        </div>
        <span className="text-[10px] text-muted-foreground">
          {attribution === "last_touch"
            ? "Credits the content that directly sourced each call"
            : "Credits the content that originally brought the lead in"}
        </span>
      </div>

      {/* Stats Summary */}
      <div className="grid gap-4 md:grid-cols-3">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card>
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm font-medium text-muted-foreground">Total Videos</p>
                  <p className="text-xl md:text-3xl font-bold">{filteredTotals?.totalVideos || 0}</p>
                </div>
                <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-[var(--brand-primary)]/10 flex items-center justify-center">
                  <span className="text-[var(--brand-primary)]"><VideoIcon /></span>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card>
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm font-medium text-muted-foreground">Total Calls</p>
                  <p className="text-xl md:text-3xl font-bold">{formatNumber(filteredTotals?.totalCalls || 0)}</p>
                </div>
                <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <span className="text-blue-500"><PhoneIcon /></span>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card>
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm font-medium text-muted-foreground">Total Cash Collected</p>
                  <p className="text-xl md:text-3xl font-bold">{formatCurrency(filteredTotals?.totalCashCollected || 0)}</p>
                </div>
                <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-green-500/10 flex items-center justify-center">
                  <span className="text-green-500"><DollarIcon /></span>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Cash Timeseries Chart */}
      {isTimeseriesLoading ? (
        <div className="bg-card rounded-xl border border-border p-5 h-[420px] animate-pulse" />
      ) : (
        <ContentCashTimeSeries
          data={filteredTimeseriesData}
          groupBy={groupBy}
          onGroupByChange={setGroupBy}
        />
      )}

      {/* Metric Filter Buttons */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-9 gap-2"
      >
        {METRIC_FILTERS.map((filter) => {
          const Icon = filter.icon;
          const isActive = sortBy === filter.value;

          return (
            <Button
              key={filter.value}
              variant={isActive ? "default" : "outline"}
              size="sm"
              onClick={() => setSortBy(filter.value)}
              className={`h-10 w-full text-xs sm:text-sm transition-all ${isActive ? "metric-filter-active bg-[var(--brand-primary)] text-white shadow-md hover:bg-[var(--brand-primary)]/90" : ""
                }`}
            >
              <Icon />
              <span className="ml-1 sm:ml-2 truncate">{filter.label}</span>
            </Button>
          );
        })}
      </motion.div>

      {/* Video Grid */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
      >
        {currentVideos.map((video, index) => (
          <motion.div
            key={video.videoId}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 * index }}
          >
            <Card
              className={`overflow-hidden hover:shadow-lg transition-shadow group ${video.videoUrl ? "cursor-pointer" : ""}`}
              onClick={() => video.videoUrl && window.open(video.videoUrl, "_blank")}
            >
              {/* Thumbnail */}
              <VideoThumbnail
                thumbnailUrl={video.thumbnailUrl}
                title={video.title}
                videoUrl={video.videoUrl}
                isNonYouTube={video.isNonYouTube}
              />

              {/* Content */}
              <CardContent className="p-4 space-y-3">
                {/* Title + Actions */}
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-sm line-clamp-2 min-h-[2.5rem]">
                    {video.title}
                  </h3>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 px-2 text-xs"
                    onClick={(event) => handleDownloadCsv(video, event)}
                    disabled={downloadingId === video.videoId}
                    title="Download CSV"
                  >
                    {downloadingId === video.videoId ? "..." : "CSV"}
                  </Button>
                </div>

                {/* Metrics */}
                <div className="space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Cash Collected</span>
                    <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                      {formatCurrency(video.cashCollected)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Close Rate</span>
                    <span className={`font-semibold ${getCloseRateColor(video.closeRate)}`}>
                      {formatPercent(video.closeRate)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Show Rate</span>
                    <span className={`font-semibold ${getShowRateColor(video.showRate)}`}>
                      {formatPercent(video.showRate)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Applicants</span>
                    <span className="font-semibold">{formatNumber(video.applications)}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">$/Applicant</span>
                    <span className="font-semibold">{formatCurrency(video.avgCashPerApplication)}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Opt-ins</span>
                    <span className="font-semibold">{formatNumber(video.optins)}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">$/Opt-in</span>
                    <span className="font-semibold">{formatCurrency(video.avgCashPerOptin)}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Booked Calls</span>
                    <span className="font-semibold">{formatNumber(video.bookedCalls)}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Closed Calls</span>
                    <span className="font-semibold">{formatNumber(video.callsClosed)}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">AOV</span>
                    <span className="font-semibold text-purple-600 dark:text-white">
                      {formatCurrency(video.aov)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="flex items-center justify-center gap-2 mt-8 mb-4 p-4 bg-card border border-border rounded-lg"
        >
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            <ChevronLeftIcon />
            <span className="ml-1">Previous</span>
          </Button>

          <div className="flex items-center gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <Button
                key={page}
                variant={currentPage === page ? "default" : "outline"}
                size="sm"
                onClick={() => setCurrentPage(page)}
                className={`min-w-[2.5rem] ${currentPage === page ? "bg-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/90" : ""
                  }`}
              >
                {page}
              </Button>
            ))}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            <span className="mr-1">Next</span>
            <ChevronRightIcon />
          </Button>
        </motion.div>
      )}

      {/* Page Info */}
      <div className="text-center text-sm text-muted-foreground">
        Page {currentPage} of {totalPages} | Showing {currentVideos.length} videos (Total: {filteredVideos.length})
      </div>

      {/* Detailed Metrics Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="bg-card rounded-xl border border-border"
      >
        <div className="p-4 border-b border-border">
          <h3 className="text-lg font-semibold">Content Performance Details</h3>
          <p className="text-sm text-muted-foreground">Detailed metrics for all tracked content</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left p-4 font-medium text-muted-foreground">Video Title</th>
                <th className="text-right p-4 font-medium text-muted-foreground">Total Calls</th>
                <th className="text-right p-4 font-medium text-muted-foreground">Closed</th>
                <th className="text-right p-4 font-medium text-muted-foreground">Show Rate</th>
                <th className="text-right p-4 font-medium text-muted-foreground">Close Rate</th>
                <th className="text-right p-4 font-medium text-muted-foreground">Cash Collected</th>
                <th className="text-right p-4 font-medium text-muted-foreground">AOV</th>
                <th className="text-right p-4 font-medium text-muted-foreground">Cash/Call</th>
              </tr>
            </thead>
            <tbody>
              {filteredVideos.slice(0, 20).map((video, i) => (
                <tr
                  key={video.videoId}
                  className={`border-b border-border hover:bg-muted/30 transition-colors ${i % 2 === 0 ? "" : "bg-muted/20"}`}
                >
                  <td className="p-4">
                    <div className="flex items-center gap-3 max-w-md">
                      {video.thumbnailUrl && (
                        <img
                          src={video.thumbnailUrl}
                          alt=""
                          className="w-16 h-9 object-cover rounded"
                        />
                      )}
                      <div className="flex flex-col">
                        <span className="font-medium line-clamp-1">{video.title}</span>
                        {video.videoUrl && (
                          <a
                            href={video.videoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-muted-foreground hover:text-[var(--brand-primary)] flex items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            View on YouTube
                            <ExternalLinkIcon />
                          </a>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-right">{formatNumber(video.totalCalls)}</td>
                  <td className="p-4 text-right">{formatNumber(video.callsClosed)}</td>
                  <td className="p-4 text-right">
                    <span className={`font-medium ${getShowRateColor(video.showRate)}`}>
                      {formatPercent(video.showRate)}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <span className={`font-medium ${getCloseRateColor(video.closeRate)}`}>
                      {formatPercent(video.closeRate)}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <span className="font-medium text-emerald-600 dark:text-emerald-400">
                      {formatCurrency(video.cashCollected)}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <span className="text-purple-600 dark:text-white font-medium">
                      {formatCurrency(video.aov)}
                    </span>
                  </td>
                  <td className="p-4 text-right">{formatCurrency(video.cashPerCall)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredVideos.length > 20 && (
          <div className="p-4 text-center text-sm text-muted-foreground border-t border-border">
            Showing top 20 of {filteredVideos.length} videos
          </div>
        )}
      </motion.div>
    </div>
  );
}
